<template>
  <div class="app-container">
    <!-- Header -->
    <AppHeader 
      :isAuthenticated="isAuthenticated"
      :currentUser="currentUser"
      :lastUpdateTime="lastUpdateTime"
    />
    
    <!-- Main Content Area -->
    <main class="main-content">
      <router-view />
    </main>
    
    <!-- Footer -->
        <AppFooter v-if="isAuthenticated" />
    <ToastNotification />
  </div>
</template>

<script>
import { ref, provide, onMounted, computed } from 'vue';
import { isAuthenticated, currentUser, authInitialized, login as authLogin, logout as authLogout, initAuth } from './utils/auth';
import apiClient from './api';
import AppHeader from './components/AppHeader.vue';
import AppFooter from './components/AppFooter.vue';
import ToastNotification from './components/ToastNotification.vue';

export default {
  name: 'App',
  components: {
    AppHeader,
    AppFooter,
    ToastNotification,
  },
  setup() {
    const lastUpdateTimestamp = ref(null);

    // Use the login and logout functions from auth.js
    const login = authLogin;
    const logout = authLogout;

    const fetchLastUpdateTime = async () => {
      try {
        // Use authAxios for authenticated requests
        const response = await apiClient.get('/sys/update_time');
        lastUpdateTimestamp.value = response.data.last_update_time;
      } catch (error) {
        console.error('Failed to fetch last update time:', error);
        lastUpdateTimestamp.value = null;
      }
    };

    const lastUpdateTime = computed(() => {
      if (!lastUpdateTimestamp.value) {
        return '無資料';
      }
      
      const date = new Date(lastUpdateTimestamp.value * 1000);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffMins < 1) {
        return '剛剛';
      } else if (diffMins < 60) {
        return `${diffMins} 分鐘前`;
      } else if (diffHours < 24) {
        return `${diffHours} 小時前`;
      } else if (diffDays < 7) {
        return `${diffDays} 天前`;
      } else {
        return date.toLocaleDateString('zh-TW', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    });

    onMounted(async () => {
      // Initialize authentication state if not already done
      if (!authInitialized.value) {
        await initAuth();
      }
      
      // Fetch last update time periodically if authenticated
      if (isAuthenticated.value) {
        fetchLastUpdateTime();
        setInterval(fetchLastUpdateTime, 5 * 60 * 1000); // Every 5 minutes
      }
    });

    // Provide login function to children components (e.g., Login.vue)
    provide('login', login);

    return {
      isAuthenticated,
      currentUser,
      login,
      logout,
      lastUpdateTime,
    };
  },
};
</script>

<style>
.app-container {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: #f8f9fa;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

body {
  margin: 0;
  padding: 0;
}

.main-content {
  flex: 1;
  min-height: calc(100vh - 120px); /* 扣除 header 和 footer 的高度 */
}
</style>